import ExportDocument from '@Application/Beneficiaries/Documents/Commands/ExportDocument';
import exportDocument from '@Application/Beneficiaries/Documents/exportDocument';
import { PermissionEnum } from '@Application/Security/Roles';
import { theme } from '@Guidelines/Theme/theme';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import DocumentViewerContext from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Providers/DocumentViewerProvider/DocumentViewerContext';
import DocumentExportWarning from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/DocumentVerificationHeader/DocumentExportWarning';
import useDocumentRoute from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Hooks/useDocumentRoute';
import editingFieldSignal from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Signals/editingFieldSignal';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import { css } from '@emotion/react';
import { Download, TriangleAlert } from 'lucide-react';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector } from '@verclam/synapse';
import { Button, ConfirmDialog } from '@verclam/ui-kit';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';

const actionGroupStyle = css`
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 0.375rem;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		align-items: stretch;
	}
`;

const helpTextStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
	text-align: right;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		text-align: left;
	}
`;

/**
 * "Télécharger (Word)": assembles the document's current, saved content into a `.docx` file entirely
 * in the browser. Confirms first only when the data itself warrants it — a not-addressed or
 * still-to-verify count above zero — never re-derived from the document's validation status.
 */
const DownloadDocumentAction: React.FC = () => {
	const dispatch = useDispatch();
	const document = useSelector((state) => state.documentsStore.document);
	const hasPermission = useHasPermission(PermissionEnum.DOCUMENT_EXPORT);
	const editingField = useSignalValue(editingFieldSignal);
	const { facilityId, subject, documentId } = useDocumentRoute();

	const [isConfirmVisible, setIsConfirmVisible] = React.useState(false);
	const [isExporting, setIsExporting] = React.useState(false);

	const notAddressedCount =
		document?.progress.byStatus.find((entry) => entry.status.value === 'not_addressed')?.count ?? 0;
	const remainingCount = document?.progress.remaining ?? 0;
	const isDirty = editingField?.isDirty === true;
	// Read without requiring the provider: this action also renders outside the document viewer.
	const isTemplateUnreadable = React.useContext(DocumentViewerContext)?.isTemplateUnreadable === true;

	const handleHideConfirm = React.useCallback(() => {
		setIsConfirmVisible(false);
	}, []);

	const handleStartExport = React.useCallback(() => {
		setIsExporting(true);
		dispatch(exportDocument(new ExportDocument(facilityId, subject, documentId)))
			.catch(ignoreHandledError)
			.finally(() => {
				setIsExporting(false);
				setIsConfirmVisible(false);
			});
	}, [dispatch, facilityId, subject, documentId]);

	const handleClick = React.useCallback(() => {
		if (notAddressedCount > 0 || remainingCount > 0) {
			setIsConfirmVisible(true);
			return;
		}

		handleStartExport();
	}, [notAddressedCount, remainingCount, handleStartExport]);

	if (!hasPermission || document === null) {
		return null;
	}

	return (
		<div css={actionGroupStyle}>
			<Button
				type="button"
				label="Télécharger (Word)"
				icon={<Download size={16} />}
				severity="secondary"
				outlined
				loading={isExporting}
				disabled={isDirty || isTemplateUnreadable}
				onClick={handleClick}
			/>
			<ConditionalLoader data={isTemplateUnreadable}>
				<p css={helpTextStyle}>Le gabarit est illisible : le document ne peut pas être téléchargé.</p>
			</ConditionalLoader>
			<ConditionalLoader data={isDirty && !isTemplateUnreadable}>
				<p css={helpTextStyle}>
					Enregistrez ou abandonnez la modification en cours pour télécharger.
				</p>
			</ConditionalLoader>
			<ConfirmDialog
				visible={isConfirmVisible}
				onHide={handleHideConfirm}
				header="Télécharger ce document ?"
				icon={<TriangleAlert size={24} aria-hidden="true" />}
				message={
					<DocumentExportWarning notAddressed={notAddressedCount} remaining={remainingCount} />
				}
				acceptLabel="Télécharger"
				rejectLabel="Retour à l'édition"
				accept={handleStartExport}
				reject={handleHideConfirm}
				loading={isExporting}
			/>
		</div>
	);
};

export default DownloadDocumentAction;
